import { effectiveWebIdentifiers } from '@heybesafe/shared';
import type { VaultItem } from '@heybesafe/shared';
import { domainFromUrl } from './domainFromUrl';

const HOST_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

// Multi-part public suffixes where the registrable domain is the last THREE
// labels (e.g. bbc.co.uk), not two. Curated subset — mirrors the set the
// extension popup uses for active-tab matching. No full Public Suffix List
// dependency.
const MULTI_PART_SLDS: ReadonlySet<string> = new Set([
  'co.uk', 'ac.uk', 'org.uk', 'net.uk', 'gov.uk',
  'co.jp', 'co.kr', 'co.in', 'co.id', 'co.za', 'co.nz', 'co.il',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au',
  'com.br', 'org.br', 'gov.br', 'edu.br',
  'com.mx', 'com.ar', 'com.cn', 'com.tw', 'com.tr',
  'com.sg', 'com.hk', 'com.my', 'com.ph', 'com.pk', 'com.vn',
  'org.nz', 'net.nz', 'ac.nz',
]);

/**
 * The registrable domain (eTLD+1) of a hostname. Strips `www.` and any other
 * leading sub-labels so we request the brand's favicon — `adidas.com` for
 * `www.adidas.com`, `twilio.com` for `login.twilio.com`. Multi-part suffixes
 * (`bbc.co.uk`) keep three labels. Returns the input unchanged for IPs,
 * single-label hosts, and non-host strings (so a fallback item name passes
 * through untouched).
 */
export function registrableDomain(host: string): string {
  const h = (host || '').toLowerCase().replace(/^www\./, '');
  if (!h || h.includes(':') || /^[\d.]+$/.test(h) || !h.includes('.')) return h;
  const labels = h.split('.');
  if (labels.length <= 2) return h;
  const lastTwo = labels.slice(-2).join('.');
  return MULTI_PART_SLDS.has(lastTwo) ? labels.slice(-3).join('.') : lastTwo;
}

// Use DuckDuckGo's favicon service (claims no logging:
// https://duckduckgo.com/duckduckgo-help-pages/privacy/icons/).
//
// We request the REGISTRABLE DOMAIN (eTLD+1), not the raw hostname: DDG keys
// its icons by registrable domain, so a saved `www.adidas.com` or
// `login.twilio.com` must resolve to `adidas.com` / `twilio.com`. Requesting
// the subdomain returns DDG's 48×48 grey-globe placeholder with HTTP 404,
// which an <img> decodes as a successful load — so there's no reliable
// client-side way to tell that miss from a real 48×48 icon. Collapsing to the
// registrable domain up front avoids that ambiguity (and the 404 console
// noise) and is what the user expects the icon to represent.
export function faviconUrl(domain: string): string {
  if (!domain) return '';
  const host = registrableDomain(domain);
  if (!HOST_RE.test(host)) return '';
  return `https://icons.duckduckgo.com/ip3/${host}.ico`;
}

/**
 * Ordered, deduped favicon-candidate URLs for an item — one per web identifier,
 * each collapsed to its registrable domain (faviconUrl handles that). HbsSiteIcon
 * walks the list on load-error before falling back to the letter tile, so an
 * item with several identifiers can still surface a favicon if the first misses.
 * Returns [] for items with no parseable web identifier (e.g. secure notes,
 * passkeys without an rp URL), which yields the letter-tile fallback.
 */
export function candidateFaviconUrls(item: VaultItem): string[] {
  return Array.from(new Set(
    effectiveWebIdentifiers(item)
      .map((u) => faviconUrl(domainFromUrl(u, '')))
      .filter((u) => u.length > 0),
  ));
}
